import React, { type FC } from 'react'
import type { SxProps, Theme } from '@mui/material'

import { isReadonlyDate } from '../../../../../utils'

import { ConnectwareError, selectLicense, StatusType, Translation } from '../../../../../domain'

import { CircularLoader, Status, Table } from '../../../common'
import { ErrorMessage } from '../../../ErrorMessage'
import { FormattedDateTime, FormattedTranslation } from '../../../Internationalization'
import { useAppState } from '../../../State'
import { LicenseId } from './LicenseId'
import { NoLicense } from './NoLicense'

const errorStyle: SxProps<Theme> = { py: 2 }
const titleStyle: SxProps<Theme> = { width: 250 }

export const LicenseDetails: FC = () => {
    const license = useAppState(selectLicense)
    if (!license) {
        return <CircularLoader />
    }

    if (ConnectwareError.is(license)) {
        return <ErrorMessage sx={errorStyle} error={license} extras="popover" />
    }

    if (!license.activated) {
        return <NoLicense />
    }

    return (
        <Table
            data-testid="license-table"
            hideHeader
            columns={{
                title: { customCellRender: (value) => <FormattedTranslation id={value} />, sx: titleStyle },
                data: {
                    customCellRender: (value, { title }) => {
                        if (typeof value === 'boolean') {
                            return <Status status={value ? StatusType.VALID : StatusType.INVALID} />
                        }

                        if (isReadonlyDate(value)) {
                            return <FormattedDateTime date={value} format="dateOnly" />
                        }

                        if (title === Translation.LICENSE_ID) {
                            return <LicenseId id={value} />
                        }

                        return value || <FormattedTranslation id={Translation.NOT_AVAILABLE} />
                    },
                },
            }}
            data={[
                { title: Translation.LICENSE_NAME, data: license.name },
                { title: Translation.LICENSE_ACCOUNT, data: license.account },
                { title: Translation.EXPIRATION_DATE, data: license.expiration },
                { title: Translation.LICENSE_CLASS, data: license.class },
                { title: Translation.LICENSE_ID, data: license.id },
                { title: Translation.LICENSE_STATUS, data: license.valid },
            ]}
        />
    )
}
